import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AppLogo } from "@/components/ui/AppLogo";
import type { Theme } from "@/context/themeContextDef";

const navItems = [
  { path: "/products", label: "Products" },
  { path: "/story", label: "Story" },
  { path: "/connect", label: "Connect" },
  { path: "/privacy-policy", label: "Privacy" },
  { path: "/terms", label: "Terms" },
];

interface NavProps {
  theme: Theme;
  onToggleTheme: () => void;
}

export function Nav({ theme, onToggleTheme }: Readonly<NavProps>) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="flex items-center justify-between py-2 md:py-3 px-4 md:px-8 max-w-[1470px] mx-auto w-full">
      {/* Logo */}
      <Link to="/" className="hover:opacity-80 transition-opacity">
        <AppLogo size="sm" />
      </Link>

      {/* Desktop Navigation Links + Theme Toggle */}
      <div className="hidden md:flex items-center gap-8">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={cn(
              "text-sm font-medium transition-colors",
              location.pathname === item.path || location.pathname.startsWith(item.path + "/")
                ? "text-[--olive]"
                : "text-[--text-secondary] hover:text-[--text-primary]"
            )}
          >
            {item.label}
          </Link>
        ))}
        
        {/* Desktop Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleTheme}
          className="rounded-full"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? <Sun className="size-4 text-yellow-500" /> : <Moon className="size-4 text-blue-400" />}
        </Button>
      </div>

      {/* Mobile: Theme Toggle + Hamburger */}
      <div className="flex md:hidden items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleTheme}
          className="rounded-full"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? <Sun className="size-4 text-yellow-500" /> : <Moon className="size-4 text-blue-400" />}
        </Button>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Open menu"
        >
          <Menu className="size-5 text-[--olive]" />
        </Button>
      </div>

      {/* Mobile Menu Sheet */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="right" className="bg-[--bg-secondary] border-[--border-primary] w-[280px]">
          <SheetHeader className="text-left">
            <SheetTitle>
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="hover:opacity-80 transition-opacity inline-block"
              >
                <AppLogo size="sm" />
              </Link>
            </SheetTitle>
          </SheetHeader>

          <div className="mt-8 flex flex-col gap-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "px-4 py-3 rounded-lg text-base font-medium transition-colors",
                  location.pathname === item.path || location.pathname.startsWith(item.path + "/")
                    ? "bg-[--olive]/10 text-[--olive]"
                    : "text-[--text-secondary] hover:bg-[--bg-tertiary] hover:text-[--text-primary]"
                )}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Footer in mobile menu */}
          <div className="absolute bottom-8 left-6 right-6 text-center text-xs text-[--text-muted]">
            <p>asking why since 2020</p>
          </div>
        </SheetContent>
      </Sheet>
    </nav>
  );
}
